import React, { useState, useEffect } from 'react'
import { Modal, Button, FormControl, FormGroup, HelpBlock } from 'react-bootstrap'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import ReactSelect, { Option } from 'react-select'

import Label from 'live/components/forms/ui/label'
import LiveTooltip from 'live/components/ui/tooltip'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { RoundedModal } from '../../../../../generic'
import { createBarrier } from '../../../../../services'
import { IAsset, IBarrier } from '../../../../../types'
import { FormContainer, ButtonContainer, FooterContainer } from './styles'

type IProps = {
    show: boolean
    handleClose: () => void
    handleSave: (id: number) => void
    asset: IAsset
    assets: IAsset[]
    item?: IBarrier /* item will be the same listItem change it when back ready */
    updateModeMethods: { id: number; label: string; value: string }[]
}

interface IFormData {
    name: string
    asset: Option<number>
    updateModeMethods: ISelect
}

interface ISelect {
    value: string
    label: string
}

const formDefault: IFormData = {
    name: '',
    asset: { label: null, value: null },
    updateModeMethods: { label: null, value: null }
}

export const AddBarrierModal: React.FC<IProps> = ({
    handleClose,
    handleSave,
    show,
    asset,
    assets,
    item,
    updateModeMethods
}) => {
    const { handleSubmit, control, formState, setValue, watch } = useForm<IFormData>({
        mode: 'all',
        defaultValues: formDefault
    })
    const [manualValidation, setManualValidation] = useState(false)
    const [createLoading, setCreateLoading] = useState(false)
    const [assetSelectList, setAssetSelectList] = useState<Option<number>[]>([])

    const description = watch('name')

    const handleCloseProtocol = (): void => {
        setValue('name', '', { shouldValidate: true })
        setValue('asset', { value: asset.id, label: asset.name }, { shouldValidate: true })
        setCreateLoading(false)
        setManualValidation(false)

        handleClose()
    }

    const handleCreate: SubmitHandler<IFormData> = async (data): Promise<void> => {
        try {
            setCreateLoading(true)
            const updateModeSelected: { value: string; label: string; id?: number } = data?.updateModeMethods
            const result = await createBarrier({
                name: data.name,
                root: true,
                asset: assets.find(el => el.id === data.asset.value),
                method: {
                    id: updateModeSelected.id
                }
            })
            handleSave(result.id)
            handleCloseProtocol()
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on create barrier.'))
        } finally {
            setCreateLoading(false)
        }
    }

    useEffect(() => {
        if (item) {
            item.asset?.assetType &&
                setValue('asset', { label: item.asset.name, value: item.asset.id }, { shouldValidate: true })
            setValue('name', item.name, { shouldValidate: true })
            setManualValidation(true)
        }
    }, [item]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (assets) {
            setAssetSelectList(
                assets?.map(asset => {
                    return { label: asset.name, value: asset.id }
                })
            )
        }
    }, [assets])

    useEffect(() => {
        if (asset) {
            setValue('asset', asset ? { value: asset?.id, label: asset?.name?.toString() } : null, {
                shouldValidate: true
            })
        }
    }, [asset]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (description && asset) {
            setManualValidation(true)
        }
    }, [description, asset])

    return (
        <RoundedModal show={show} onHide={handleCloseProtocol}>
            <form onSubmit={handleSubmit(handleCreate)}>
                <Modal.Header>
                    <Modal.Title id="contained-modal-title">{i18n('New Barrier')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormContainer>
                        <Controller
                            rules={{ required: true }}
                            name="asset"
                            defaultValue={asset ? { value: asset.id, label: asset.name } : null}
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={!field.value && fieldState.error ? 'error' : 'success'}>
                                    <Label required className="control-label">
                                        {i18n('Resource')}
                                    </Label>
                                    <ReactSelect
                                        clearable={false}
                                        id="barrierList-createModal-field-asset"
                                        options={assetSelectList}
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                    />
                                </FormGroup>
                            )}
                        />
                        <Controller
                            rules={{
                                required: true,

                                validate: value =>
                                    value.trim() === item?.name
                                        ? i18n('Please insert a different name when copying barrier')
                                        : true
                            }}
                            name="name"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup
                                    validationState={
                                        manualValidation && (!field.value || fieldState.error) ? 'error' : 'success'
                                    }
                                >
                                    <Label required className="control-label">
                                        {i18n('Name')}
                                    </Label>

                                    <FormControl
                                        type="text"
                                        componentClass="textarea"
                                        id="barrierList-createModal-field-name"
                                        ref={field.ref}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        maxLength={255}
                                    />
                                    {fieldState.error && <HelpBlock>{fieldState.error.message}</HelpBlock>}
                                </FormGroup>
                            )}
                        />
                        {/* #TODO lower case just upper case in first letter */}
                        <Controller
                            rules={{ required: true }}
                            name="updateModeMethods"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FormGroup validationState={!field.value && fieldState.error ? 'error' : 'success'}>
                                    <Label required className="control-label">
                                        {i18n('Method')}
                                    </Label>
                                    <LiveTooltip
                                        content={i18n('For a semi-qualitative bowtie qualitative barriers are used.')}
                                        position="top"
                                    >
                                        <ReactSelect
                                            clearable={false}
                                            id="barrierList-createModal-field-method"
                                            options={updateModeMethods
                                                .sort((a, b) => a.label.localeCompare(b.label))
                                                .map(method => ({
                                                    id: method.id,
                                                    value: method.value,
                                                    label: i18n(method.label)
                                                }))}
                                            ref={field.ref}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            value={field.value}
                                        />
                                    </LiveTooltip>
                                </FormGroup>
                            )}
                        />
                    </FormContainer>
                </Modal.Body>
                <Modal.Footer>
                    <FooterContainer>
                        <ButtonContainer>
                            <Button
                                className="no-outline-button"
                                bsStyle="primary"
                                type="submit"
                                id="barrierList-createModal-button-create"
                                disabled={!manualValidation || !formState.isValid}
                            >
                                {createLoading ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin" /> {i18n('Creating')}
                                    </>
                                ) : (
                                    i18n('Create')
                                )}
                            </Button>
                            <Button
                                className="no-outline-button"
                                type="button"
                                id="barrierList-createModal-button-close"
                                onClick={handleCloseProtocol}
                            >
                                {i18n('Close')}
                            </Button>
                        </ButtonContainer>
                        <span>{i18n('Required')} *</span>
                    </FooterContainer>
                </Modal.Footer>
            </form>
        </RoundedModal>
    )
}
